import React, { useEffect, useReducer, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, ProgressBar, Row, Table } from 'react-bootstrap';
import {
  deleteGarminKey,
  deleteMyAccount,
  exportMyData,
  getGarminStatus,
  getSyncStatus,
  putGarminKey,
  startSync,
} from '../api/trainer';
import { errorMessage, retryAfterSeconds, serverMessage, statusOf } from '../api/errors';
import { GarminConnectionStatus, GarminStatus, SyncStatus, SyncTypeStatus } from '../api/types';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { SYNC_TYPE_LABELS, daysBetween, formatDate, formatDateTime, isoDaysAgo } from '../utils/format';

const DELETE_CONFIRMATION = 'DELETE';
/** Expiry choices for a new key, in days. The backend accepts 1 to 365. */
export const KEY_EXPIRY_CHOICES = [30, 90, 180, 365] as const;
const DEFAULT_EXPIRY_DAYS = 90;
/** Warn this many days before a key expires. */
const EXPIRY_WARNING_DAYS = 14;

const STATUS_TEXT: Record<GarminConnectionStatus, string> = {
  disconnected: 'Not connected',
  ok: 'Connected',
  invalid: 'Key rejected',
  expired: 'Key expired',
  revoked: 'Key revoked',
};

const STATUS_VARIANT: Record<GarminConnectionStatus, string> = {
  disconnected: 'secondary',
  ok: 'success',
  invalid: 'danger',
  expired: 'warning',
  revoked: 'danger',
};

/** Warning text for the key, or null when the key needs no attention. */
export function keyWarning(g: GarminStatus): { variant: 'warning' | 'danger'; text: string } | null {
  if (!g.connected) return null;
  if (g.status === 'expired') {
    return { variant: 'warning', text: 'This key has expired, so syncing has stopped. Create a new key and save it below.' };
  }
  if (g.status === 'invalid' || g.status === 'revoked') {
    return { variant: 'danger', text: 'Garmin no longer accepts this key. Create a new key and save it below.' };
  }
  if (g.daysUntilExpiry != null && g.daysUntilExpiry <= EXPIRY_WARNING_DAYS) {
    const days = Math.max(0, g.daysUntilExpiry);
    return {
      variant: 'warning',
      text: `This key expires in ${days} ${days === 1 ? 'day' : 'days'}. Create a new key before then and save it here.`,
    };
  }
  return null;
}

/** Backfill progress for one type, as a percentage and a label. */
export function backfillProgress(type: SyncTypeStatus, today: string): { percent: number; label: string } {
  const { backfill } = type;
  if (backfill.done) return { percent: 100, label: 'Complete' };
  const total = Math.max(1, daysBetween(backfill.floor, today));
  if (!backfill.cursor) return { percent: 0, label: 'Not started' };
  const covered = Math.max(0, Math.min(total, daysBetween(backfill.cursor, today)));
  return { percent: Math.round((covered / total) * 100), label: `${covered} of ${total} days` };
}

const SettingsPage: React.FC = () => {
  const garmin = useAsync(() => getGarminStatus(), []);
  const sync = useAsync(() => getSyncStatus(), []);

  // The key only lives in this input until it is sent. It is never displayed back.
  const [key, setKey] = useState('');
  const [expiryDays, setExpiryDays] = useState<number>(DEFAULT_EXPIRY_DAYS);
  const [keyMessage, setKeyMessage] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
  const [keyBusy, setKeyBusy] = useState(false);

  const [syncData, setSyncData] = useState<SyncStatus | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ variant: 'success' | 'danger' | 'warning'; text: string } | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const [exportError, setExportError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleted, setDeleted] = useState(false);

  // Count the cooldown down on screen.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const id = window.setInterval(() => rerender(), 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

  const g = garmin.data;
  const types = (syncData ?? sync.data)?.types ?? [];
  const today = isoDaysAgo(0);
  const warning = g ? keyWarning(g) : null;
  const canSync = Boolean(g?.connected && g.status === 'ok');

  const saveKey = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) {
      setKeyMessage({ variant: 'danger', text: 'Paste your Garmin Personal API key first.' });
      return;
    }
    setKeyBusy(true);
    setKeyMessage(null);
    try {
      const status = await putGarminKey({ apiKey: trimmed, expiresInDays: expiryDays });
      setKey('');
      const text = status.status === 'ok' ? 'Key saved. The first sync will start shortly.' : 'Key saved.';
      setKeyMessage({ variant: 'success', text });
      garmin.reload();
      sync.reload();
    } catch (err) {
      setKeyMessage({ variant: 'danger', text: errorMessage(err) });
    } finally {
      setKeyBusy(false);
    }
  };

  const removeKey = async () => {
    setKeyBusy(true);
    setKeyMessage(null);
    try {
      await deleteGarminKey();
      setKeyMessage({ variant: 'success', text: 'Garmin key removed.' });
      garmin.reload();
      sync.reload();
    } catch (err) {
      setKeyMessage({ variant: 'danger', text: errorMessage(err) });
    } finally {
      setKeyBusy(false);
    }
  };

  const syncNow = async () => {
    setSyncBusy(true);
    setSyncMessage(null);
    try {
      const status = await startSync();
      setSyncData(status);
      setSyncMessage({ variant: 'success', text: 'Sync started. New activities and health data appear in a few minutes.' });
    } catch (err) {
      if (statusOf(err) === 429) {
        const seconds = retryAfterSeconds(err) ?? 300;
        setCooldownUntil(Date.now() + seconds * 1000);
        setSyncMessage({ variant: 'warning', text: serverMessage(err) ?? 'A sync was started recently. Try again later.' });
      } else {
        setSyncMessage({ variant: 'danger', text: errorMessage(err) });
      }
    } finally {
      setSyncBusy(false);
    }
  };

  const exportData = async () => {
    setExportBusy(true);
    setExportError(null);
    try {
      const file = await exportMyData();
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(errorMessage(err));
    } finally {
      setExportBusy(false);
    }
  };

  const deleteAccount = async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteMyAccount();
      setShowDelete(false);
      setDeleted(true);
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  if (deleted) {
    return (
      <>
        <PageHeader title="Settings" />
        <Alert variant="success">
          All your trainer data was deleted. Continue to log in again if you want to start a new account.
        </Alert>
        <Button variant="primary" onClick={() => window.location.assign('/')}>
          Continue
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Settings">
        <p>Connect Garmin, check syncing, and manage your data.</p>
      </PageHeader>

      <Row className="g-3">
        <Col lg={7}>
          <section className="surface h-100">
            <h2>Garmin Personal API key</h2>
            <StatusMessage loading={garmin.loading} error={garmin.error} onRetry={garmin.reload} />
            {g && (
              <p className="mb-3 d-flex flex-wrap align-items-center gap-2">
                <Badge bg={STATUS_VARIANT[g.status]}>{STATUS_TEXT[g.status]}</Badge>
                {g.connected && (
                  <span className="muted small">
                    {g.keyExpiresAt ? `Expires ${formatDate(g.keyExpiresAt)}` : 'No expiry date set'}
                    {g.lastCheckedAt ? ` · last checked ${formatDateTime(g.lastCheckedAt)}` : ''}
                  </span>
                )}
              </p>
            )}
            {warning && (
              <Alert variant={warning.variant} className="py-2">
                {warning.text}
              </Alert>
            )}

            <details className="mb-3 small">
              <summary>How to create a key in Garmin Connect</summary>
              <ol className="mt-2 mb-0">
                <li>Sign in to Garmin Connect in your browser.</li>
                <li>
                  Open Garmin&apos;s Personal API key page (it is part of the Garmin Connect developer access) and create a new
                  key. Menu names change, so follow Garmin&apos;s current instructions for Personal API keys.
                </li>
                <li>Copy the key and paste it below. It is checked with Garmin, stored encrypted, and never shown again.</li>
                <li>Pick how long the key should last. When it expires, create a new key and save it here.</li>
              </ol>
            </details>

            <Form onSubmit={saveKey} autoComplete="off">
              <Row className="g-2">
                <Col sm={7}>
                  <Form.Group controlId="garmin-key" className="mb-2">
                    <Form.Label>API key</Form.Label>
                    <Form.Control
                      type="password"
                      value={key}
                      onChange={e => setKey(e.target.value)}
                      autoComplete="new-password"
                      spellCheck={false}
                      placeholder="Paste key"
                      disabled={keyBusy}
                    />
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group controlId="garmin-expiry" className="mb-2">
                    <Form.Label>Key lasts</Form.Label>
                    <Form.Select
                      value={expiryDays}
                      onChange={e => setExpiryDays(Number(e.target.value))}
                      disabled={keyBusy}
                    >
                      {KEY_EXPIRY_CHOICES.map(days => (
                        <option key={days} value={days}>
                          {days} days
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              {keyMessage && <Alert variant={keyMessage.variant} className="py-2">{keyMessage.text}</Alert>}
              <div className="d-flex flex-wrap gap-2">
                <Button type="submit" variant="primary" disabled={keyBusy}>
                  Save key
                </Button>
                {g?.connected && (
                  <Button variant="outline-danger" onClick={removeKey} disabled={keyBusy}>
                    Disconnect
                  </Button>
                )}
              </div>
            </Form>
          </section>
        </Col>

        <Col lg={5}>
          <section className="surface h-100">
            <h2>Sync</h2>
            <StatusMessage loading={sync.loading} error={sync.error} onRetry={sync.reload} />
            {!sync.loading && !sync.error && types.length > 0 && (
              <div className="table-responsive mb-3">
                <Table size="sm" className="trainer-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Last run</th>
                      <th>History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.map(t => {
                      const progress = backfillProgress(t, today);
                      return (
                        <tr key={t.type}>
                          <td>
                            {SYNC_TYPE_LABELS[t.type]}
                            {t.lastStatus === 'ERROR' && t.lastError && (
                              <div className="stat-hint text-danger">{t.lastError}</div>
                            )}
                          </td>
                          <td className="small">
                            {t.lastRunAt ? formatDateTime(t.lastRunAt) : 'Never'}
                            {t.lastStatus && (
                              <Badge bg={t.lastStatus === 'OK' ? 'success' : 'danger'} className="ms-1">
                                {t.lastStatus === 'OK' ? 'OK' : 'Error'}
                              </Badge>
                            )}
                          </td>
                          <td className="small">
                            <ProgressBar now={progress.percent} style={{ height: 6 }} className="mb-1" aria-label={`${SYNC_TYPE_LABELS[t.type]} history`} />
                            {progress.label}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            {types.length > 0 && (
              <p className="small muted">History goes back to {formatDate(types[0].backfill.floor)}.</p>
            )}
            {syncMessage && <Alert variant={syncMessage.variant} className="py-2">{syncMessage.text}</Alert>}
            {!canSync && g && (
              <p className="small muted">Connect a valid Garmin key to sync.</p>
            )}
            <Button
              variant="outline-primary"
              onClick={syncNow}
              disabled={!canSync || syncBusy || cooldownLeft > 0}
            >
              {cooldownLeft > 0 ? `Sync again in ${Math.ceil(cooldownLeft / 60)} min` : 'Sync now'}
            </Button>
          </section>
        </Col>

        <Col xs={12}>
          <section className="surface">
            <h2>Your data</h2>
            <p className="muted small">
              Export everything we hold about you as JSON, or delete your account with all activities, health data and your
              Garmin key.
            </p>
            {exportError && <Alert variant="warning" className="py-2">Export: {exportError}</Alert>}
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={exportData} disabled={exportBusy}>
                Export my data
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => {
                  setConfirmText('');
                  setDeleteError(null);
                  setShowDelete(true);
                }}
              >
                Delete my data
              </Button>
            </div>
          </section>
        </Col>
      </Row>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete all your data?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This removes your activities, health data, records and Garmin key. It cannot be undone.</p>
          <Form.Group controlId="delete-confirm">
            <Form.Label>Type {DELETE_CONFIRMATION} to confirm</Form.Label>
            <Form.Control value={confirmText} onChange={e => setConfirmText(e.target.value)} autoComplete="off" />
          </Form.Group>
          {deleteError && <Alert variant="danger" className="mt-3 mb-0 py-2">{deleteError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={confirmText !== DELETE_CONFIRMATION || deleteBusy} onClick={deleteAccount}>
            Delete everything
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SettingsPage;
